<template>
  <!-- PlacementSection yalnızca SSR tarafında placement bazlı karoselleri
       render eder. Backend ürünleri hazır gönderdiği için ekstra lazy-load
       mantığına ihtiyaç yoktur. -->
  <div v-if="sections.length" class="max-w-7xl mx-auto sm:px-6 lg:px-8 space-y-6">
    <FrontendProductCarouselSection
      v-for="section in sections"
      :key="section.id"
      :title="section.title"
      :products="section.products"
      :loading="isLoading"
      :view-all-link="section.viewAllLink"
      :banner="section.banner"
      :banners="section.banners"
      :banner-alt="section.title"
      @add-to-cart="(payload) => emit('add-to-cart', payload)"
      @toggle-favorite="(payload) => emit('toggle-favorite', payload)"
    />
  </div>
</template>

<script setup>

const props = defineProps({
  placement: {
    type: String,
    required: true
  },
  location: {
    type: String,
    default: 'product'
  },
  categoryId: {
    type: [Number, String, null],
    default: null
  },
  itemsLimit: {
    type: Number,
    default: 12
  }
})

const emit = defineEmits(['add-to-cart', 'toggle-favorite'])

const normalizeId = (value) => {
  const numeric = Number(value)
  return Number.isNaN(numeric) ? null : numeric
}

const normalizedCategoryId = computed(() => normalizeId(props.categoryId))

const carouselParams = computed(() => {
  const params = {
    location: props.location,
    placement: props.placement,
    include_items: true,
    items_limit: props.itemsLimit
  }

  const categoryId = normalizedCategoryId.value
  if (categoryId !== null) {
    params.category_id = categoryId
  }

  return params
})

const cacheKey = computed(
  () => `placement-${props.location}-${props.placement}-${normalizedCategoryId.value ?? 'none'}`
)

const {
  carousels,
  pending: isLoading,
  refresh
} = await useCarousels({
  key: cacheKey.value,
  params: carouselParams,
  watch: [cacheKey],
  immediate: normalizedCategoryId.value !== null || props.categoryId === null
})

watch(normalizedCategoryId, (newVal, oldVal) => {
  if (newVal !== oldVal && newVal !== null) {
    refresh()
  }
})

const extractCarouselItems = (carousel) => {
  if (!carousel) {
    return []
  }

  const items = carousel.items

  if (Array.isArray(items)) {
    return items
  }

  if (items && typeof items === 'object') {
    if (Array.isArray(items.data)) {
      return items.data
    }

    const collection = items.data?.['Illuminate\\Support\\Collection']
    if (Array.isArray(collection)) {
      return collection
    }
  }

  return []
}

const sections = computed(() =>
  (carousels.value ?? [])
    .map((carousel) => ({
      id: carousel.id,
      title: carousel.name,
      products: extractCarouselItems(carousel),
      viewAllLink: carousel.view_all_url,
      banner: carousel.banner,
      banners: carousel.banners
    }))
    .filter((section) => section.products.length > 0)
)
</script>
